import { Link, useParams } from "react-router";
import { Button, Card, EmptyState, Stack } from "@salesapp/ui";
import "./pages.css";

const SUPPORT_EMAIL = "support@saleslox.com";

export const HTTP_ERROR_PAGES: Record<
  number,
  { title: string; description: string; action: string; href: string }
> = {
  400: {
    title: "That request was not valid",
    description: "Check what you entered and try again. Nothing on the server was changed.",
    action: "Go back home",
    href: "/dashboard",
  },
  401: {
    title: "Please sign in",
    description: "Your session is missing or has ended. Sign in to continue — we will not keep you on a confidential screen.",
    action: "Sign in",
    href: "/login",
  },
  403: {
    title: "You do not have access",
    description: "This area is limited to people with permission. Ask an organization admin if you need it.",
    action: "Go to dashboard",
    href: "/dashboard",
  },
  404: {
    title: "Page not found",
    description: "The page you are looking for does not exist, has moved, or the link is incomplete.",
    action: "Go to dashboard",
    href: "/dashboard",
  },
  408: {
    title: "The request timed out",
    description: "The connection took too long. Try again in a moment.",
    action: "Try again",
    href: "/dashboard",
  },
  409: {
    title: "This record changed",
    description: "Someone else updated this while you were working. Refresh and try again.",
    action: "Go to dashboard",
    href: "/dashboard",
  },
  410: {
    title: "That item is gone",
    description: "It was removed or is no longer available. Pick it again from the list if you still need it.",
    action: "Go to dashboard",
    href: "/dashboard",
  },
  413: {
    title: "That file is too large",
    description: "Use a smaller file and try again. Nothing was stored.",
    action: "Go back",
    href: "/dashboard",
  },
  422: {
    title: "Some fields need attention",
    description: "Fix the highlighted information and submit again. No server details are shown here.",
    action: "Go back",
    href: "/dashboard",
  },
  429: {
    title: "Too many attempts",
    description: "Wait a moment, then try again. This protects the service from automated abuse.",
    action: "Back to sign in",
    href: "/login",
  },
  500: {
    title: "Something went wrong",
    description: "We recorded the problem for our team. Try again, or come back in a few minutes.",
    action: "Go to dashboard",
    href: "/dashboard",
  },
  502: {
    title: "The service is unreachable",
    description: "A connection between services failed. Wait a moment and try again.",
    action: "Try again",
    href: "/dashboard",
  },
  503: {
    title: "Temporarily unavailable",
    description: "The workspace is down for a short time. Try again shortly.",
    action: "Try again",
    href: "/dashboard",
  },
  504: {
    title: "The service took too long",
    description: "The request did not finish in time. Try again in a moment.",
    action: "Try again",
    href: "/dashboard",
  },
};

export function errorPageFor(code: number) {
  return HTTP_ERROR_PAGES[code] ?? HTTP_ERROR_PAGES[500];
}

export function ErrorPage({ code }: { code?: number }) {
  const params = useParams();
  const parsed = code ?? Number(params.code);
  const status = Number.isInteger(parsed) && parsed >= 400 && parsed < 600 ? parsed : 404;
  const page = errorPageFor(status);
  return (
    <main className="error-page">
      <Card padding="lg">
        <Stack gap={5} align="center">
          <a href="https://saleslox.com" target="_blank" rel="noopener noreferrer" className="error-page-brand-link">
            <img src="/logo-mark.svg" alt="" className="error-page-brand-mark" />
          </a>
          <EmptyState
            title={`${status} · ${page.title}`}
            description={page.description}
            action={
              <Link to={page.href}>
                <Button variant="primary">{page.action}</Button>
              </Link>
            }
          />
          <p className="error-page-support">
            Still stuck? Contact{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="error-page-support-link">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </Stack>
      </Card>
    </main>
  );
}
